import { Component, OnInit } from "@angular/core";
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from "primeng/dynamicdialog";
import { IssuerMappingService } from "../../../../../api/services/issuer-mapping.service";
import { MappingFilter } from "../../../../../api/models/mapping-filter";
import { IssuerToMap } from "../../../../../api/models/issuer-to-map";
import { Issuer } from "../../../../../api/models/issuer";
import { MessageService } from "primeng/api";
import { AsReportedIssuerService } from "../../../../../api/services/as-reported-issuer.service";
import { Form144Service } from "../../../../../core/services/form144.service";
import { Form144Issuer } from "../../../../../core/services/custom-interface/issuer-form144.model";
import { sanitizeForm } from "../../../../../core/utils/form-clean";
import {
  handlePasteWithoutLeadingSpaces,
  preventLeadingSpaceKey,
} from "src/core/utils/input-validation.utils";

type FormData = {
  asReportedIssuerId: string;
  asReportedCik: string;
  asReportedEin: string;
  asReportedStateOfIncorporation: string;
  asReportedSic: string;
  asReportedFileNumber: string;
  asReportedFiscalYearEnd: string;
  asReportedIssuerName: string;
  issuerId: number | undefined;
  mapWithCompanyId: boolean;
};

// Only string keys of formData
type StringFormDataKeys = {
  [K in keyof FormData]: FormData[K] extends string ? K : never;
}[keyof FormData];
type PasteField = StringFormDataKeys | keyof MapDataComponent;

@Component({
  selector: "app-map-data",
  // imports: [],
  templateUrl: "./map-data.component.html",
  styleUrl: "./map-data.component.css",
  standalone: false,
})
export class MapDataComponent implements OnInit {
  issuers: any[] = [];
  selectedIssuers: any[] = [];
  selectedDialogRow: any = null;
  searchIssuer: string = "";
  searchIssuerByCik: string = "";
  duplicateCiks: Set<string> = new Set();
  remap: boolean = false;
  issuerFirst = 0;
  issuerRows = 10;
  showDropdown = false;
  formData = {
    asReportedIssuerId: "",
    asReportedCik: "",
    asReportedEin: "",
    asReportedStateOfIncorporation: "",
    asReportedSic: "",
    asReportedFileNumber: "",
    asReportedFiscalYearEnd: "",
    asReportedIssuerName: "",
    issuerId: undefined as number | undefined,
    mapWithCompanyId: false,
  };
  isLoading: boolean = false;
  filteredIssuers: any[] = [];
  selectedIssuer: any = null;
  searchForm144Issuer: string = "";
  form144FilteredIssuers: Form144Issuer[] = [];
  showForm144Dropdown = false;
  validationErrors = {
    asReportedIssuerName: "",
    asReportedCik: "",
  };
  constructor(
    public config: DynamicDialogConfig,
    public ref: DynamicDialogRef,
    private _issuerMappingService: IssuerMappingService,
    private _asReportedIssuerService: AsReportedIssuerService,
    public dialogService: DialogService,
    private _messageService: MessageService,
    private _form144Service: Form144Service
  ) {}

  ngOnInit(): void {
    this.selectedIssuers = this.config.data.issuers;
    this.issuers = [...this.config.data.issuers];
    this.remap = this.config.data.remap;
    // Calculate duplicates initially
    this.findDuplicateCiks();
    if (this.selectedIssuers.length === 1) {
      const issuer = this.selectedIssuers[0];
      this.searchIssuer = issuer?.asReportedIssuerName || "";
      this.searchIssuerByCik = issuer?.asReportedCik || "";
      this.onSearchEnter();
    }
  }

  private bindFormData(product: any) {
    this.formData = {
      asReportedIssuerId: product.asReportedIssuerId || "",
      asReportedCik: product.asReportedCik || "",
      asReportedEin: product.asReportedEin || "",
      asReportedStateOfIncorporation:
        product.asReportedStateOfIncorporation || "",
      asReportedSic: product.asReportedSic || "",
      asReportedFileNumber: product.asReportedFileNumber || "",
      asReportedFiscalYearEnd: product.asReportedFiscalYearEnd || "",
      asReportedIssuerName: product.asReportedIssuerName || "",
      issuerId:
        typeof product.issuerId === "number" ? product.issuerId : undefined,
      mapWithCompanyId: false,
    };
  }

  private clearFormData() {
    this.formData = {
      asReportedIssuerId: "",
      asReportedCik: "",
      asReportedEin: "",
      asReportedStateOfIncorporation: "",
      asReportedSic: "",
      asReportedFileNumber: "",
      asReportedFiscalYearEnd: "",
      asReportedIssuerName: "",
      issuerId: undefined,
      mapWithCompanyId: false,
    };
    // Clear validation errors when form is cleared
    this.validationErrors = {
      asReportedIssuerName: "",
      asReportedCik: "",
    };
  }

  onDialogRowSelect(event: any) {
    this.selectedDialogRow = event.data;
    this.bindFormData(event.data);
  }
  onDialogRowUnselect(product: any) {
    if (this.formData?.asReportedIssuerId === product.data.asReportedIssuerId) {
      this.clearFormData();
    }
  }
  onSubmit() {
    // Reset validation errors
    this.validationErrors = {
      asReportedIssuerName: "",
      asReportedCik: "",
    };

    // Validate required fields
    let isValid = true;
    if (!this.formData.asReportedIssuerName || !this.formData.asReportedIssuerName.trim()) {
      this.validationErrors.asReportedIssuerName = "Issuer Name is required";
      isValid = false;
    }
    if (!this.formData.asReportedCik || !this.formData.asReportedCik.trim()) {
      this.validationErrors.asReportedCik = "CIK is required";
      isValid = false;
    }

    if (!isValid) {
     
      return;
    }

    const cleanedForm = sanitizeForm(this.formData);

    const reason = this.remap
      ? "Remapped to correct curated it successfully"
      : "Mapped to curated id successfully";
    const params: IssuerToMap = {
      asReportedIssuerIds: this.issuers
        .map((issuer) => issuer.asReportedIssuerId)
        .filter((id): id is number => typeof id === "number"),
      reason: reason,
      theIssuer: {
        cikNumber: cleanedForm.asReportedCik?.trim() || undefined,
        ein: cleanedForm.asReportedEin ?.trim() || undefined,
        fileNumber: cleanedForm.asReportedFileNumber ?.trim() || undefined,
        fiscalYearEnd: cleanedForm.asReportedFiscalYearEnd ?.trim() || undefined,
        issuerName: cleanedForm.asReportedIssuerName ?.trim() ?? "",
        sicNumber: cleanedForm.asReportedSic?.trim() || undefined,
        stateOfIncorporation: cleanedForm.asReportedStateOfIncorporation?.trim() || undefined,
        issuerId: cleanedForm.mapWithCompanyId
          ? cleanedForm.issuerId
          : undefined,
        // Optional: Add others like irsNumber, reason, totalCount if needed
      },
    };
    this.isLoading = true;
    this._issuerMappingService.doMapTheIssuer(params).subscribe({
      next: (value) => {},
      error: (err) => {
        this.isLoading = false;

        let message = "Something went wrong";

        try {
          const parsed = JSON.parse(err.error); 
          message = parsed.message || message;
        } catch (e) {
          message = err.error || message;
        }

        this._messageService.add({
          severity: "error",
          summary: "Error",
          detail: message,
        });
      },

      complete: () => {
        this.isLoading = false;
        this.ref.close({ refresh: true });
      },
    });
  }

  onCancel() {
    this.ref.close();
  }
  onSearchEnter() {
    const query = this.searchIssuer.trim();
    const cik = this.searchIssuerByCik.trim();

    if (!query && !cik) return;

    this.isLoading = true;
    let mergedResults: any[] = [];
    const nameCall = query
      ? this._asReportedIssuerService.getAsReportedIssuerByName(query)
      : null;

    const cikCall = cik
      ? this._asReportedIssuerService.getAsReportedIssuerByCik(cik)
      : null;

    if (nameCall) {
      nameCall.subscribe({
        next: (nameData) => {
          if (nameData && nameData.length) {
            mergedResults = mergedResults.concat(nameData);
          }

          if (cikCall) {
            cikCall.subscribe({
              next: (cikData) => {
                if (cikData && cikData.length) {
                  mergedResults = mergedResults.concat(cikData);
                }
                this.processMergedResults(mergedResults);
              },
              error: (err) => {
                this.isLoading = false;
                this._messageService.add({
                  severity: "error",
                  summary: "Error fetching issuer by CIK",
                  detail: err.error?.message || "",
                });
              },
            });
          } else {
            this.processMergedResults(mergedResults);
          }
        },
        error: (err) => {
          this.isLoading = false;
          this._messageService.add({
            severity: "error",
            summary: "Error fetching issuer by name",
            detail: err.error?.message || "",
          });
        },
      });
    } else if (cikCall) {
      cikCall.subscribe({
        next: (cikData) => {
          mergedResults = cikData || [];
          this.processMergedResults(mergedResults);
        },
        error: (err) => {
          this.isLoading = false;
          this._messageService.add({
            severity: "error",
            summary: "Error fetching issuer by CIK",
            detail: err.error?.message || "",
          });
        },
      });
    }
  }

  // Helper to assign merged results to table and dropdown
  private processMergedResults(results: any[]) {
    // Remove duplicates by asReportedIssuerId
    const mergedResults = results.filter(
      (v, i, a) =>
        a.findIndex((t) => t.asReportedIssuerId === v.asReportedIssuerId) === i
    );
    this.filteredIssuers = mergedResults;
    this.selectedIssuers = mergedResults;
    this.showDropdown = mergedResults.length > 0;
    // Recalculate duplicates after new data
    this.findDuplicateCiks();
    if (mergedResults.length === 0) {
      this._messageService.add({
        severity: "info",
        summary: "No issuer found",
      });
    }

    this.isLoading = false;
  }

  onIssuerSelect(issuer: any) {
    this.selectedIssuer = issuer;
    this.searchIssuer = issuer.asReportedIssuerName || "";
    this.showDropdown = false;
    this.selectedIssuers = [issuer]; // Update table
  }
  // Call this whenever selectedIssuers changes
  private findDuplicateCiks() {
    const cikCounts: Record<string, number> = {};
    this.selectedIssuers.forEach((issuer) => {
      const cik = issuer.asReportedCik;
      if (cik) {
        cikCounts[cik] = (cikCounts[cik] || 0) + 1;
      }
    });

    this.duplicateCiks = new Set(
      Object.keys(cikCounts).filter((cik) => cikCounts[cik] > 1)
    );
  }
  preventSpaceKey(event: KeyboardEvent): void {
    preventLeadingSpaceKey(event);
  }
  // One universal paste handler for ALL fields
  onPaste(event: ClipboardEvent, field: PasteField): void {
    handlePasteWithoutLeadingSpaces(event, (newValue: string) => {
      if (field in this.formData) {
        // @ts-ignore
        this.formData[field as StringFormDataKeys] = newValue;
      } else {
        // @ts-ignore
        (this as any)[field] = newValue;
      }
    });
  }
}
